import logger from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/layout/Navbar';
import { MobileNav } from '../components/layout/MobileNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Settings,
  Globe,
  Key,
  Plus,
  Trash2,
  Copy,
  Loader2,
  User,
  CheckCircle,
  HelpCircle,
  ShoppingCart
} from 'lucide-react';
import { WooCommerceGuide } from '../components/WooCommerceGuide';

export default function SettingsPage() {
  const { t, language, setLanguage } = useLanguage();
  const { user, api, updateProfile } = useAuth();
  const navigate = useNavigate();
  
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    language: user?.language || language
  });
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newKeyDialog, setNewKeyDialog] = useState(false);
  const [newKeyData, setNewKeyData] = useState({ shop_name: '', shop_url: '' });
  const [createdKey, setCreatedKey] = useState(null);
  const [showWooGuide, setShowWooGuide] = useState(false);

  const fetchApiKeys = useCallback(async () => {
    try {
      const response = await api.get('/woocommerce/api-keys');
      setApiKeys(response.data.api_keys || []);
    } catch (error) {
      logger.error('Error fetching API keys:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await updateProfile({
        name: profileData.name,
        language: profileData.language
      });
      setLanguage(profileData.language);
      toast.success(t('settings.saved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!newKeyData.shop_name || !newKeyData.shop_url) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/woocommerce/api-key', newKeyData);
      setCreatedKey(response.data.api_key);
      fetchApiKeys();
      toast.success('API key created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeKey = async (apiKeyId) => {
    try {
      await api.delete(`/woocommerce/api-key/${apiKeyId}`);
      fetchApiKeys();
      toast.success('API key revoked');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      
      <main className="max-w-2xl mx-auto px-4 py-6 sm:py-8">
        <Button 
          variant="ghost" 
          className="mb-4" 
          onClick={() => navigate('/dashboard')}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('dashboard.welcome').split(',')[0]}
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Settings className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">{t('settings.title')}</h1>
        </div>

        <div className="space-y-6">
          {/* Profile Settings */}
          <Card className="animate-fade-in-up">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {t('settings.profile')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('auth.name')}</Label>
                <Input
                  id="name"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  className="h-12 rounded-xl"
                  data-testid="settings-name"
                />
              </div>

              <div className="space-y-2">
                <Label>{t('auth.email')}</Label>
                <Input
                  value={user?.email || ''}
                  disabled
                  className="h-12 rounded-xl bg-muted"
                />
              </div>

              <Button 
                onClick={handleSaveProfile} 
                disabled={loading}
                className="rounded-xl"
                data-testid="save-profile"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t('settings.save')}
              </Button>
            </CardContent>
          </Card>

          {/* Language Settings */}
          <Card className="animate-fade-in-up stagger-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                {t('settings.language')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Select 
                value={profileData.language} 
                onValueChange={(value) => {
                  setProfileData({ ...profileData, language: value });
                  setLanguage(value);
                }}
              >
                <SelectTrigger className="h-12 rounded-xl" data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* API Keys */}
          <Card className="animate-fade-in-up stagger-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="h-5 w-5" />
                    {t('settings.api_keys')}
                  </CardTitle>
                  <CardDescription className="mt-1">{t('settings.api_keys_desc')}</CardDescription>
                </div>
                <Button 
                  onClick={() => {
                    setNewKeyData({ shop_name: '', shop_url: '' });
                    setCreatedKey(null);
                    setNewKeyDialog(true);
                  }}
                  size="sm"
                  className="rounded-full"
                  data-testid="create-api-key"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t('settings.create_key')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {apiKeys.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Key className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No API keys yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {apiKeys.map((key) => (
                    <div 
                      key={key.api_key_id}
                      className={`flex items-center justify-between p-4 rounded-xl border ${
                        key.is_active ? 'border-border' : 'border-destructive/30 bg-destructive/5'
                      }`}
                      data-testid={`api-key-${key.api_key_id}`}
                    >
                      <div>
                        <p className="font-medium">{key.shop_name}</p>
                        <p className="text-sm text-muted-foreground">{key.shop_url}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Created: {new Date(key.created_at).toLocaleDateString()}
                          {!key.is_active && <span className="text-destructive ml-2">(Revoked)</span>}
                        </p>
                      </div>
                      {key.is_active && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleRevokeKey(key.api_key_id)}
                          data-testid={`revoke-${key.api_key_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* WooCommerce Installation Guide */}
          <Card className="animate-fade-in-up stagger-3 border-2 border-dashed border-primary/30 bg-primary/5">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    {language === 'fr' ? 'Intégration WooCommerce' : 'WooCommerce Integration'}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {language === 'fr' 
                      ? 'Apprenez à intégrer PayHub avec votre boutique WordPress'
                      : 'Learn how to integrate PayHub with your WordPress store'
                    }
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => setShowWooGuide(true)}
                  className="gap-2"
                  data-testid="user-open-guide-btn"
                >
                  <HelpCircle className="h-4 w-4" />
                  {language === 'fr' ? 'Guide d\'Installation' : 'Installation Guide'}
                </Button>
              </div>
            </CardHeader>
          </Card>
        </div>
      </main>

      {/* Create API Key Dialog */}
      <Dialog open={newKeyDialog} onOpenChange={setNewKeyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('settings.create_key')}</DialogTitle>
            <DialogDescription>
              Create an API key to integrate PayHub with your WooCommerce store.
            </DialogDescription>
          </DialogHeader>

          {createdKey ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <p className="text-sm text-green-700 dark:text-green-400">API key created successfully!</p>
              </div>
              
              <div className="space-y-2">
                <Label>Your API Key</Label>
                <div className="flex gap-2">
                  <Input 
                    value={createdKey} 
                    readOnly 
                    className="font-mono text-sm"
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyToClipboard(createdKey)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-destructive">
                  Save this key now! It won't be shown again.
                </p>
              </div>

              <DialogFooter>
                <Button onClick={() => setNewKeyDialog(false)}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="shop_name">{t('settings.shop_name')}</Label>
                <Input
                  id="shop_name"
                  placeholder="My Store"
                  value={newKeyData.shop_name}
                  onChange={(e) => setNewKeyData({ ...newKeyData, shop_name: e.target.value })}
                  data-testid="shop-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="shop_url">{t('settings.shop_url')}</Label>
                <Input
                  id="shop_url"
                  placeholder="https://mystore.com"
                  value={newKeyData.shop_url}
                  onChange={(e) => setNewKeyData({ ...newKeyData, shop_url: e.target.value })}
                  data-testid="shop-url-input"
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setNewKeyDialog(false)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleCreateApiKey} disabled={loading} data-testid="confirm-create-key">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {t('settings.create_key')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* WooCommerce Installation Guide */}
      <WooCommerceGuide 
        language={language}
        isOpen={showWooGuide}
        onClose={() => setShowWooGuide(false)}
        adminKey={null}
      />

      <MobileNav />
    </div>
  );
}
